import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useT, type DictionaryKey } from '@dia/i18n';
import { loadShell } from '@dia/content';
import {
  addPartyMember,
  cacheRows,
  cachedRows,
  claimRoom,
  deadlineState,
  describeBeds,
  fillOf,
  listBedTypes,
  listHouses,
  listRoomClaims,
  listRooms,
  moveClaim,
  progressOf,
  releaseClaim,
  setRoomPickDeadline,
  useOutbox,
  whyNotClaim,
  type BedType,
  type Guest,
  type House,
  type Room,
  type RoomClaim,
  type StayGuest,
} from '@dia/db';
import { Button, LangToggle, Screen, StatusPill, SyncBadge } from '@dia/ui';
import { useAuth } from '../auth';
import { supabase, sync } from '../supabase';
import { BackLink } from './parts/BackLink';
import { RoomPhoto } from './parts/RoomPhoto';
import { useAccess } from './parts/useAccess';
import { claimErrorKey, groupRooms, myClaim, occupantsOf, unplaced } from './logic/rooms';

const content = loadShell('guest');

/** The one house-cover photo we have, so a guest sees the house before its bedrooms. */
const HOUSE_PHOTOS: Record<string, string> = {
  hacienda: '/photos/hacienda.webp',
  bungalow: '/photos/bungalow.webp',
};

/**
 * Who sleeps where.
 *
 * Each guest picks their own room, first come first served. The booker (the
 * stay's lead guest) may pick for anybody in the group, move them afterwards,
 * and set an optional deadline. A guest only ever sees their own stay.
 *
 * Reads work offline: the rooms, the beds and the last known claims all come
 * from the phone's cache when there is no signal. Writes do not, and that is
 * deliberate. A claim queued in the outbox would hand somebody a bed that
 * another phone took twenty minutes earlier, so a claim is confirmed with the
 * island or it does not happen, and the screen says which.
 *
 * The only write here that does go through the outbox is a contact detail on
 * the party list, because nothing races for it.
 */
export function Rooms() {
  const { t, lang } = useT();
  const { session } = useAuth();
  const access = useAccess();
  const { enqueue, pending, dead, online, syncing } = useOutbox(sync);

  const [rooms, setRooms] = useState<Room[]>([]);
  const [houses, setHouses] = useState<House[]>([]);
  const [bedTypes, setBedTypes] = useState<BedType[]>([]);
  const [claims, setClaims] = useState<RoomClaim[]>([]);
  const [party, setParty] = useState<Guest[]>([]);
  const [stay, setStay] = useState<StayGuest | null>(null);

  const [forGuestId, setForGuestId] = useState<string>('');
  const [busyRoom, setBusyRoom] = useState<string | null>(null);
  const [error, setError] = useState<DictionaryKey | null>(null);
  const [saved, setSaved] = useState(false);

  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newHandle, setNewHandle] = useState('');
  const [partyError, setPartyError] = useState<DictionaryKey | null>(null);
  const [deadlineDraft, setDeadlineDraft] = useState('');

  const stayId = session?.stay.id ?? null;
  const meId = session?.guest.id ?? null;
  const isBooker = session?.guest.is_lead === true;

  const load = useCallback(async () => {
    if (!stayId) return;

    const [roomRows, houseRows, beds, claimRows] = await Promise.all([
      listRooms(supabase),
      listHouses(supabase),
      listBedTypes(supabase),
      listRoomClaims(supabase, stayId),
    ]);
    setRooms(roomRows);
    setHouses(houseRows);
    setBedTypes(beds);
    setClaims(claimRows);

    const entity = `guests:${stayId}`;
    try {
      const { data, error: err } = await supabase.from('guests').select('*').eq('stay_id', stayId);
      if (err) throw new Error(err.message);
      setParty(await cacheRows(entity, (data ?? []) as Guest[]));
    } catch {
      setParty(await cachedRows<Guest>(entity));
    }

    try {
      // stays_guest, not stays (migration 019, defect D4).
      const { data } = await supabase.from('stays_guest').select('*').eq('id', stayId).maybeSingle();
      if (data) {
        await cacheRows('stays', [data as StayGuest]);
        setStay(data as StayGuest);
      } else {
        setStay((await cachedRows<StayGuest>('stays')).find((s) => s.id === stayId) ?? null);
      }
    } catch {
      setStay((await cachedRows<StayGuest>('stays')).find((s) => s.id === stayId) ?? null);
    }
  }, [stayId]);

  useEffect(() => {
    // A read that throws must not leave a blank screen: the cache is the whole
    // point of this page on a boat. Anything unexpected becomes the plain
    // error line and the guest can still see what the phone already had.
    void load().catch((err) => {
      console.error('rooms load', err);
      setError(claimErrorKey(err instanceof Error ? err.message : ''));
    });
  }, [load, online, pending]);

  const groups = useMemo(() => groupRooms(rooms, houses), [rooms, houses]);
  const deadline = useMemo(
    () => deadlineState(stay?.room_pick_deadline ?? null),
    [stay?.room_pick_deadline],
  );
  const mine = useMemo(() => myClaim(claims, meId), [claims, meId]);
  const waiting = useMemo(() => unplaced(party, claims), [party, claims]);

  // The booker picks for whoever is still standing; everybody else picks for
  // themselves and the control is not drawn at all.
  const target = isBooker ? forGuestId || meId : meId;
  const targetClaim = claims.find((c) => c.guest_id === target) ?? null;

  if (!session || !access) return null;

  const partySize = Math.max(
    party.length,
    (stay?.adults ?? 0) + (stay?.children ?? 0),
    claims.length,
  );
  const progress = progressOf(rooms, claims, partySize);
  const name = (g: Guest | null) => (g ? `${g.first_name} ${g.last_name}`.trim() : '');
  const roomName = (room: Room) => (lang === 'en' ? room.name_en : room.name_es);

  async function run(job: () => Promise<unknown>, roomId: string) {
    setBusyRoom(roomId);
    setError(null);
    setSaved(false);
    try {
      await job();
      setSaved(true);
      await load();
    } catch (err) {
      setError(claimErrorKey(err instanceof Error ? err.message : ''));
      // Somebody else may have taken it. Show the island's truth, not ours.
      await load();
    } finally {
      setBusyRoom(null);
    }
  }

  const onTake = (room: Room) => {
    if (!stayId || !target) return;
    if (targetClaim) {
      void run(
        () =>
          moveClaim(supabase, targetClaim.id, room.id, {
            claimedBy: target === meId ? 'guest' : 'booker',
            claimedByGuestId: meId,
          }),
        room.id,
      );
      return;
    }
    void run(
      () =>
        claimRoom(supabase, {
          stayId,
          roomId: room.id,
          guestId: target,
          claimedBy: target === meId ? 'guest' : 'booker',
          claimedByGuestId: meId,
        }),
      room.id,
    );
  };

  const onRelease = (claim: RoomClaim) => {
    void run(() => releaseClaim(supabase, claim.id), claim.room_id);
  };

  const onAddPerson = async (event: FormEvent) => {
    event.preventDefault();
    if (!stayId) return;
    if (!newFirst.trim() || !newLast.trim()) {
      setPartyError('guest.rooms.party_needs_name');
      return;
    }
    setPartyError(null);
    try {
      await addPartyMember(supabase, {
        stayId,
        firstName: newFirst.trim(),
        lastName: newLast.trim(),
        email: newEmail.trim() || null,
        socialHandle: newHandle.trim() || null,
      });
      setNewFirst('');
      setNewLast('');
      setNewEmail('');
      setNewHandle('');
      await load();
    } catch (err) {
      setPartyError(claimErrorKey(err instanceof Error ? err.message : ''));
    }
  };

  /** A contact detail races with nothing, so it is the one thing that queues. */
  const onContact = async (guest: Guest, field: 'email' | 'social_handle', value: string) => {
    await enqueue({
      table: 'guests',
      kind: 'update',
      payload: { [field]: value.trim() || null },
      match: { id: guest.id },
    });
    setParty((rows) => rows.map((r) => (r.id === guest.id ? { ...r, [field]: value } : r)));
  };

  const onDeadline = async (value: string | null) => {
    try {
      await setRoomPickDeadline(supabase, value);
      await load();
    } catch (err) {
      setError(claimErrorKey(err instanceof Error ? err.message : ''));
    }
  };

  return (
    <Screen
      title={t('guest.rooms.screen_title')}
      lede={t('guest.rooms.lede')}
      actions={
        <>
          <SyncBadge pending={pending} dead={dead} online={online} syncing={syncing} />
          <LangToggle />
        </>
      }
    >
      <BackLink />

      {!access.personalOpen ? (
        <p className="mt-2 text-inkMuted" role="status">
          {t('guest.request.locked')}
        </p>
      ) : (
        <div className="mt-2 flex flex-col gap-10">
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <StatusPill tone="info">
                {t('guest.rooms.progress', { placed: progress.placed, people: progress.people })}
              </StatusPill>
              <StatusPill tone="neutral">
                {t('guest.rooms.rooms_used', { used: progress.roomsUsed, total: progress.rooms })}
              </StatusPill>
            </div>

            <p className="text-inkMuted">
              {mine
                ? t('guest.rooms.mine', {
                    room: roomName(rooms.find((r) => r.id === mine.room_id) ?? rooms[0]!),
                  })
                : t('guest.rooms.mine_none')}
            </p>

            {waiting.length > 0 && (
              <p className="text-inkFaint text-caption">
                {t('guest.rooms.unplaced', { names: waiting.map((g) => g.first_name).join(', ') })}
              </p>
            )}

            {deadline.kind === 'open' && (
              <p role="status" className="text-mark">
                {t('guest.rooms.deadline_open', { hours: deadline.hoursLeft })}
              </p>
            )}
            {deadline.kind === 'passed' && (
              <p role="status" className="text-warn">
                {t('guest.rooms.deadline_passed')}
              </p>
            )}

            {!online && (
              <p role="status" className="rounded border border-line bg-surface p-3 text-inkMuted">
                {t('guest.rooms.offline')}
              </p>
            )}

            {error && (
              <p role="alert" className="text-danger">
                {t(error)}
              </p>
            )}
            {saved && !error && (
              <p role="status" className="text-ok">
                {t('guest.rooms.party_saved')}
              </p>
            )}
          </section>

          {isBooker && party.length > 1 && (
            <section className="flex flex-col gap-2">
              <label className="flex flex-col gap-2">
                <span>{t('guest.rooms.pick_who')}</span>
                <select
                  value={target ?? ''}
                  onChange={(e) => setForGuestId(e.target.value)}
                  className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                >
                  {party.map((g) => (
                    <option key={g.id} value={g.id}>
                      {name(g)}
                      {g.id === meId ? ` (${t('guest.rooms.you')})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </section>
          )}

          {groups.map((group, i) => {
            const isFirstForHouse = groups.findIndex((g) => g.houseSlug === group.houseSlug) === i;
            const houseDoc = content.section('houses').find((d) => d.slug === group.houseSlug);
            const housePhoto = HOUSE_PHOTOS[group.houseSlug];

            return (
            <section key={group.key} aria-labelledby={`g-${group.key}`} className="flex flex-col gap-4">
              {isFirstForHouse && (housePhoto || houseDoc) && (
                <div className="-mx-4 overflow-hidden rounded-b-xl">
                  {housePhoto && (
                    <img
                      src={housePhoto}
                      alt=""
                      className="h-40 w-full object-cover"
                    />
                  )}
                  {houseDoc && (
                    <p className="bg-surface px-4 py-3 text-inkMuted">{houseDoc.lede?.[lang]}</p>
                  )}
                </div>
              )}

              <h2 id={`g-${group.key}`} className="font-display text-[1.25em]">
                {lang === 'en' ? group.houseName.en : group.houseName.es}
                {group.floor && (
                  <span className="text-inkFaint">
                    {' '}
                    {t(`guest.rooms.floor.${group.floor}` as DictionaryKey)}
                  </span>
                )}
              </h2>

              {group.rooms.map((room) => {
                const fill = fillOf(room, claims);
                const here = occupantsOf(room.id, claims, party, meId);
                const beds = describeBeds(room.beds, bedTypes, lang);
                const forSelf = target === meId;
                const block = whyNotClaim({
                  fill,
                  deadline,
                  isBooker,
                  forSelf,
                  online,
                });
                const alreadyHere = targetClaim?.room_id === room.id;

                return (
                  <article
                    key={room.id}
                    className="flex flex-col gap-3 rounded border border-line bg-surface p-4"
                  >
                    <RoomPhoto path={room.photo_path} roomName={roomName(room)} />

                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-display text-[1.15em]">{roomName(room)}</h3>
                      {fill.full ? (
                        <StatusPill tone="neutral">{t('guest.rooms.full')}</StatusPill>
                      ) : (
                        <StatusPill tone="ok">
                          {fill.free === 1
                            ? t('guest.rooms.free_one')
                            : t('guest.rooms.free', { count: fill.free })}
                        </StatusPill>
                      )}
                    </div>

                    <ul className="text-caption text-inkMuted">
                      {room.en_suite && <li>{t('guest.rooms.en_suite')}</li>}
                      {beds.map((bed, i) => (
                        <li key={`${bed.label}-${i}`}>
                          {t('guest.rooms.bed_line', { count: bed.count, bed: bed.label })}
                          {bed.extra && <span className="text-inkFaint">, {t('guest.rooms.bed_extra')}</span>}
                        </li>
                      ))}
                      <li>
                        {room.extra_sleeps > 0
                          ? t('guest.rooms.sleeps', {
                              base: room.base_sleeps,
                              max: room.max_sleeps,
                            })
                          : t('guest.rooms.sleeps_plain', { max: room.max_sleeps })}
                      </li>
                    </ul>

                    {(lang === 'en' ? room.perk_en : room.perk_es) && (
                      <p className="text-caption text-accent">
                        {lang === 'en' ? room.perk_en : room.perk_es}
                      </p>
                    )}

                    <div>
                      <h4 className="text-caption text-inkFaint">{t('guest.rooms.in_this_room')}</h4>
                      {here.length === 0 ? (
                        <p className="text-inkFaint">{t('guest.rooms.nobody_yet')}</p>
                      ) : (
                        <ul>
                          {here.map((row) => (
                            <li
                              key={row.claim.id}
                              className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-2 last:border-b-0"
                            >
                              <span>
                                {row.guest ? name(row.guest) : ''}
                                {row.isMe && <span className="text-inkFaint"> ({t('guest.rooms.you')})</span>}
                                {row.guest?.is_lead && (
                                  <span className="text-inkFaint"> ({t('guest.rooms.booker')})</span>
                                )}
                              </span>
                              {(row.isMe || isBooker) && online && (
                                <Button
                                  variant="quiet"
                                  onClick={() => onRelease(row.claim)}
                                  busy={busyRoom === room.id}
                                  busyLabel={t('loading')}
                                >
                                  {row.isMe
                                    ? t('guest.rooms.release')
                                    : t('guest.rooms.release_person', {
                                        name: row.guest?.first_name ?? '',
                                      })}
                                </Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {!alreadyHere && (
                      <Button
                        block
                        disabled={block !== null}
                        busy={busyRoom === room.id}
                        busyLabel={t('loading')}
                        onClick={() => onTake(room)}
                      >
                        {forSelf
                          ? targetClaim
                            ? t('guest.rooms.move_here')
                            : t('guest.rooms.claim')
                          : t('guest.rooms.claim_for')}
                      </Button>
                    )}
                  </article>
                );
              })}
            </section>
            );
          })}

          {isBooker && (
            <section aria-labelledby="deadline" className="flex flex-col gap-3 border-t border-line pt-8">
              <h2 id="deadline" className="font-display text-[1.25em]">
                {t('guest.rooms.deadline_set')}
              </h2>
              <p className="text-inkFaint text-caption">{t('guest.rooms.deadline_help')}</p>
              {deadline.kind === 'none' && <p className="text-inkMuted">{t('guest.rooms.deadline_none')}</p>}
              <input
                type="datetime-local"
                value={deadlineDraft}
                onChange={(e) => setDeadlineDraft(e.target.value)}
                className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={!deadlineDraft || !online}
                  onClick={() => void onDeadline(new Date(deadlineDraft).toISOString())}
                >
                  {t('guest.rooms.deadline_set')}
                </Button>
                {deadline.kind !== 'none' && (
                  <Button variant="secondary" disabled={!online} onClick={() => void onDeadline(null)}>
                    {t('guest.rooms.deadline_clear')}
                  </Button>
                )}
              </div>
            </section>
          )}

          <section aria-labelledby="party" className="flex flex-col gap-4 border-t border-line pt-8">
            <h2 id="party" className="font-display text-[1.25em]">
              {t('guest.rooms.party_title')}
            </h2>
            <p className="text-inkMuted">{t('guest.rooms.party_lede')}</p>

            <ul>
              {party.map((g) => {
                const editable = isBooker || g.id === meId;
                return (
                  <li key={g.id} className="flex flex-col gap-2 border-b border-line py-3 last:border-b-0">
                    <span>
                      {name(g)}
                      {g.is_lead && <span className="text-inkFaint"> ({t('guest.rooms.booker')})</span>}
                    </span>
                    {editable && (
                      <div className="flex flex-col gap-2">
                        <label className="flex flex-col gap-1">
                          <span className="text-caption text-inkFaint">{t('guest.rooms.email')}</span>
                          <input
                            type="email"
                            defaultValue={g.email ?? ''}
                            onBlur={(e) => void onContact(g, 'email', e.target.value)}
                            className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-caption text-inkFaint">{t('guest.rooms.handle')}</span>
                          <input
                            type="text"
                            defaultValue={g.social_handle ?? ''}
                            onBlur={(e) => void onContact(g, 'social_handle', e.target.value)}
                            className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                          />
                        </label>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="text-inkFaint text-caption">{t('guest.rooms.contact_help')}</p>

            {isBooker && (
              <form onSubmit={onAddPerson} className="flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                  <span>{t('guest.rooms.first_name')}</span>
                  <input
                    value={newFirst}
                    onChange={(e) => setNewFirst(e.target.value)}
                    className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t('guest.rooms.last_name')}</span>
                  <input
                    value={newLast}
                    onChange={(e) => setNewLast(e.target.value)}
                    className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t('guest.rooms.email')}</span>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t('guest.rooms.handle')}</span>
                  <input
                    value={newHandle}
                    onChange={(e) => setNewHandle(e.target.value)}
                    className="min-h-tap rounded border border-line bg-surface px-3 text-ink"
                  />
                </label>

                {partyError && (
                  <p role="alert" className="text-danger">
                    {t(partyError)}
                  </p>
                )}

                <Button type="submit" disabled={!online}>
                  {t('guest.rooms.party_add')}
                </Button>
              </form>
            )}
          </section>
        </div>
      )}
    </Screen>
  );
}
